import {
  bisector,
  scaleUtc,
  extent,
  scaleLinear,
  create,
  axisLeft,
  axisRight,
  axisBottom,
  line as d3Line,
  curveLinear,
  select,
  pointer,
} from "d3";
import type { SpreadPoint } from "./definitions/SpreadPoint";

// Right-hand axis (reference chart puts values on the right, not the left) —
// left margin just needs to clear the first x-axis tick label, which is
// center-anchored under a data point that sits at x=0.
const MARGIN = { top: 16, right: 46, bottom: 26, left: 24 };

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** `"10. Jul"` — matches the reference chart's x-axis tick format. */
function formatTick(date: Date): string {
  return `${date.getUTCDate()}. ${MONTHS[date.getUTCMonth()]}`;
}

/** One plotted line — the primary symbol plus zero or more compare instruments, each its own color. */
export interface SpreadSeries {
  /** Unique key (e.g. the symbol) — used as the D3 join key so lines update in place rather than re-mounting. */
  id: string;
  /** Legend/tooltip label, e.g. `"EURUSD"`. */
  label: string;
  color: string;
  data: SpreadPoint[];
}

export interface SpreadChartOptions {
  /** Hover crosshair + value tooltip. @default false */
  showTooltip?: boolean;
  /**
   * Unit label shown after each tooltip value, e.g. `"pip"`, `"bps"`.
   * `{quote}` is replaced with the series' quote currency (last 3 characters
   * of its label), so `"{quote} pip"` renders `"GBP pip"` for EURGBP and
   * `"USD pip"` for EURUSD. Empty shows the bare value. @default ""
   */
  tooltipSpreadUnit?: string;
}

/**
 * Resolves `tooltipSpreadUnit` for one series — `{quote}` becomes the quote
 * currency (last 3 characters of the symbol; base tickers vary in length but
 * the quote currency is always 3).
 */
function formatSpreadUnit(unit: string, symbol: string): string {
  return unit.replace(/\{quote\}/g, symbol.slice(-3).toUpperCase()).trim();
}

export function renderChart(
  container: HTMLElement,
  series: SpreadSeries[],
  options: SpreadChartOptions = {},
) {
  const { showTooltip = false, tooltipSpreadUnit = "" } = options;
  const bisect = bisector<SpreadPoint, number>((d) => d[0]).left;
  let lastWidth = -1;
  let lastHeight = -1;

  function build() {
    lastWidth = container.clientWidth;
    lastHeight = container.clientHeight;
    container.innerHTML = "";
    const visible = series.filter((s) => s.data.length > 0);
    if (visible.length === 0) return;

    const width = container.clientWidth;
    const height = container.clientHeight;
    const innerWidth = width - MARGIN.left - MARGIN.right;
    const innerHeight = height - MARGIN.top - MARGIN.bottom;
    // Container is hidden/collapsed (e.g. mid-reload, panel `hidden`) — bail
    // before an SVG with negative-size attributes gets built.
    if (innerWidth <= 0 || innerHeight <= 0) return;
    
    // Shared scales across every visible series — a compare instrument with
    // a wider spread band will stretch the axis for all of them, same as
    // overlaying multiple lines on one chart normally works.
    const allPoints = visible.flatMap((s) => s.data);

    const x = scaleUtc()
      .domain(extent(allPoints, (d) => d[0]) as [number, number])
      .range([0, innerWidth]);

    // Hugs the data range (not zero-based) — the reference chart zooms into
    // the spread's actual band (~0.2-0.7) rather than showing dead space
    // down to 0.
    const [dataMin, dataMax] = extent(allPoints, (d) => d[1]) as [number, number];
    const yPad = (dataMax - dataMin) * 0.15 || dataMax * 0.1;
    const y = scaleLinear()
      .domain([Math.max(0, dataMin - yPad), dataMax + yPad])
      .nice()
      .range([innerHeight, 0]);

    const svg = create("svg")
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("role", "img")
      .attr(
        "aria-label",
        `Line chart of spread average over time for ${visible.map((s) => s.label).join(", ")}.`,
      );

    const g = svg.append("g").attr("transform", `translate(${MARGIN.left},${MARGIN.top})`);

    // Recessive horizontal gridlines only — the reference chart has no
    // vertical gridlines, just x-axis tick labels.
    g.append("g")
      .attr("class", "grid grid-y")
      .call(axisLeft(y).tickSize(-innerWidth).ticks(6).tickFormat(() => ""))
      .call((sel) => sel.select(".domain").remove());

    // Value axis on the right, matching the reference layout.
    g.append("g")
      .attr("class", "axis axis-y")
      .attr("transform", `translate(${innerWidth},0)`)
      .call(axisRight(y).ticks(6).tickSize(0).tickPadding(8))
      .call((sel) => sel.select(".domain").remove());

    g.append("g")
      .attr("class", "axis axis-x")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(
        axisBottom(x)
          .ticks(Math.min(allPoints.length, 10))
          .tickFormat((d) => formatTick(d as Date))
          .tickPadding(10),
      )
      .call((sel) => sel.select(".domain").remove());

    // Straight segments (not smoothed) — the reference chart's peaks are sharp.
    const line = d3Line<SpreadPoint>()
      .x((d) => x(d[0]))
      .y((d) => y(d[1]))
      .curve(curveLinear);

    for (const s of visible) {
      g.append("path")
        .datum(s.data)
        .attr("class", "spread-line")
        .attr("fill", "none")
        .attr("stroke", s.color)
        .attr("stroke-width", 1.75)
        .attr("stroke-linejoin", "round")
        .attr("stroke-linecap", "round")
        .attr("d", line);
    }

    if (showTooltip) {
      const focuses = new Map(
        visible.map((s) => {
          const focus = g.append("g").attr("class", "focus").style("display", "none");
          focus.append("line").attr("class", "focus-line").attr("y1", 0).attr("y2", innerHeight);
          focus
            .append("circle")
            .attr("class", "focus-dot")
            .attr("r", 4)
            .attr("fill", s.color);
          return [s.id, focus] as const;
        }),
      );

      const tooltip = select(container.parentElement).select<HTMLDivElement>(".chart-tooltip");

      g.append("rect")
        .attr("class", "hover-target")
        .attr("width", innerWidth)
        .attr("height", innerHeight)
        .attr("fill", "transparent")
        .on("mouseenter", () => {
          focuses.forEach((focus) => focus.style("display", null));
          tooltip.style("opacity", "1");
        })
        .on("mouseleave", () => {
          focuses.forEach((focus) => focus.style("display", "none"));
          tooltip.style("opacity", "0");
        })
        .on("mousemove", (event) => {
          const [mx] = pointer(event);
          const t = x.invert(mx).getTime();
          let anchorX = mx;
          let anchorLabelTime = t;

          const rows = visible.map((s) => {
            // No clamp to `length - 1`: with a single point that made `i` 0
            // and `d0` undefined. Past the end, `d1` is undefined → `d0`.
            const i = bisect(s.data, t, 1);
            const d0 = s.data[i - 1];
            const d1 = s.data[i];
            const d = d1 && t - d0[0] > d1[0] - t ? d1 : d0;
            if (d) {
              const px = x(d[0]);
              const py = y(d[1]);
              focuses.get(s.id)?.select(".focus-line").attr("x1", px).attr("x2", px);
              focuses.get(s.id)?.select(".focus-dot").attr("cx", px).attr("cy", py);
              anchorX = px;
              anchorLabelTime = d[0];
            }
            return { s, d };
          });

          const rowsHtml = rows
            .filter((r) => r.d)
            .map(
              (r) =>
                `<div class="tooltip-row"><span class="tooltip-label" style="color:${r.s.color}">${r.s.label}:</span> <strong class="tooltip-value">${(Math.floor(r.d![1] * 100) / 100).toFixed(2)}${tooltipSpreadUnit ? ` ${formatSpreadUnit(tooltipSpreadUnit, r.s.label)}` : ""}</strong></div>`,
            )
            .join("");
          if (!rowsHtml) return;

          tooltip.html(
            `<div class="tooltip-row tooltip-date">${formatTick(new Date(anchorLabelTime))}</div>${rowsHtml}`,
          );

          const tooltipEl = tooltip.node() as HTMLDivElement;
          const tooltipWidth = tooltipEl?.offsetWidth ?? 0;
          let left = MARGIN.left + anchorX + 12;
          if (left + tooltipWidth > width) left = MARGIN.left + anchorX - tooltipWidth - 12;
          tooltip.style("left", `${left}px`).style("top", `${MARGIN.top}px`);
        });
    }

    container.appendChild(svg.node()!);
  }

  build();

  // `observe()` fires once straight away, at the size `build()` just drew
  // at — skip any callback where the size hasn't actually changed.
  const resizeObserver = new ResizeObserver(() => {
    if (container.clientWidth === lastWidth && container.clientHeight === lastHeight) return;
    build();
  });
  resizeObserver.observe(container);

  return () => resizeObserver.disconnect();
}
